import type { ContentHash, ContentRef } from "@cogo/publisher-core";

import type { ApplicationEvidenceBinding } from "./application-evidence-binding";
import type { ApplicationStageBinding } from "./application-stage-binding";

/**
 * The only fetch policy an application published readback may use. A cached, credentialed,
 * redirect-following, or content-encoded fetch cannot observe the published bytes.
 */
export const APPLICATION_PUBLISHED_READBACK_FETCH_POLICY = Object.freeze({
  cache: "no-store",
  credentials: "omit",
  redirect: "error",
  acceptEncoding: "identity",
} as const);

export type ApplicationPublishedReadbackFetchPolicy = typeof APPLICATION_PUBLISHED_READBACK_FETCH_POLICY;

export type ApplicationPublishedReadbackOutcome = "matched" | "mismatched" | "unavailable";

/**
 * Fresh-session published readback evidence.
 *
 * Named `…-session-observation.v1` rather than `…-observation.v1`: the latter identity is already
 * held by the sealed Task 11 gate-observation contract for `PUBLISHED_READBACK_MATCHED`, whose
 * bytes are pinned by the core admission wire manifest.
 */
export interface ApplicationPublishedReadbackObservation {
  schemaVersion: "cogo.application-published-readback-session-observation.v1";
  binding: ApplicationEvidenceBinding & { stage: Extract<ApplicationStageBinding, { stage: "environment" }> };
  authorizationConsumptionRef: ContentRef;
  deploymentReceiptRef: ContentRef;
  sessionRef: ContentRef;
  fetchPolicy: ApplicationPublishedReadbackFetchPolicy;
  observedArtifactHash: ContentHash | null;
  observedOutputManifestHash: ContentHash | null;
  outcome: ApplicationPublishedReadbackOutcome;
  reasonCodes: readonly string[];
  observedAt: string;
  contentHash: ContentHash;
}

export const APPLICATION_PUBLISHED_READBACK_OBSERVATION_KEYS = Object.freeze([
  "schemaVersion",
  "binding",
  "authorizationConsumptionRef",
  "deploymentReceiptRef",
  "sessionRef",
  "fetchPolicy",
  "observedArtifactHash",
  "observedOutputManifestHash",
  "outcome",
  "reasonCodes",
  "observedAt",
  "contentHash",
] as const);
